import { useContext, useEffect, useRef, useState } from 'react';
import { SmartContext } from '../../../../../library/Core/SmartContext';
import { allowPerformAction, getActionElementClass, getDomainValueForCode } from '../../../../../library/Core/SmartFunctions';
import { useNavigate } from 'react-router-dom';
import { Tooltip, Modal } from 'bootstrap';
import SessionContext from '../../../../../library/Core/SessionContext';
import { canShowBenefits } from '../../service/student-profile-view.service';
import ResumeManageModal from './ResumeManageModal';

/** Domain lookups fall back to the raw code, which is often a numeric id. Only real strings can be trimmed. */
const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const domainName = (code: unknown, category: string, state: any): string => {
    if (code == null || code === '' || code === 0) return '';
    const label = getDomainValueForCode(code as any, category, state);
    if (typeof label !== 'string') return '';
    const trimmed = label.trim();
    return trimmed && trimmed !== String(code) ? trimmed : '';
};

const Journey = () => {
    const { state, dispatch } = useContext(SmartContext);
    const model = state.data.user;
    const profile = state.data.profile;
    const [isGapInJourney, setIsGapInJourney] = useState(false);
    const [validJobs, setValidJobs] = useState<any[]>([]);
    const { sessionState, sessionDispatch } = useContext(SessionContext);

    const navigate = useNavigate();
    
    useEffect(() => {
        const validJobData = state?.data?.jobsList?.filter((job: any) => {
            return !(
                   (job.companyId == null || job.companyId == 0) && !asText(job.otherCompany)
                && (job.instituteId == null || job.instituteId == 0) && !asText(job.otherInstitute)
            );
        });
        setValidJobs(validJobData);
    }, []);
    
    // Get the most recent job based on priority rules
    const getMostRecentJob = () => {
        const jobsList = validJobs || [];
        if (jobsList.length === 0) return null;

        // Priority 1: Get jobs where isCurrent == true
        const currentJobs = jobsList.filter((job: any) => job.isCurrent === true);
        
        if (currentJobs.length > 0) {
            // If multiple current jobs, pick the one with most recent startDate
            return currentJobs.reduce((latest: any, job: any) => {
                const latestStart = new Date(latest.startDate).getTime();
                const jobStart = new Date(job.startDate).getTime();
                return jobStart > latestStart ? job : latest;
            });
        }

        // Priority 2: No current job, pick the most recent by endDate
        return jobsList.reduce((latest: any, job: any) => {
            const latestEnd = new Date(latest.endDate).getTime();
            const jobEnd = new Date(job.endDate).getTime();
            return jobEnd > latestEnd ? job : latest;
        });
    };

    const formatJobDates = (job: any) => {
        if (!job) return '';
        
        const startDate = new Date(job.startDate);
        const endDate = new Date(job.endDate);
        
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        
        const isCurrentJob = job.isCurrent === true;
        const endDateStr = isCurrentJob ? 'Present' : endYear.toString();
        
        return `${startYear} - ${endDateStr}`;
    };

    const getJobDisplayName = (job: any) => {
        // Return company name or other company name if available
        // if (job?.otherCompany) return job.otherCompany;
        // // Return company ID as fallback (you may need to map this to actual company names)
        // return `Company ${job?.companyId || 'Unknown'}`;

        return job.companyId && job.companyId > 0
            ? domainName(job.companyId, 'COMPANY_CODE', state) || asText(job.otherCompany)
            : asText(job.otherCompany);
    };

    const mostRecentJob = getMostRecentJob();

    const navigateToProfileTab = (route: string) => {
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'selectedTab', value: route } });
        navigate(`/alumni/${state.data.id}/profile/${route}`);
    };

    const getKgToPgInstCount = () => {
        let count = 0;
        const instKeys = ['postGradCollegeJoined', 'underGradCollegeJoined', 'schools']
        console.log("state.data: ", state.data)
        instKeys.forEach((key) => {
            if (state.data[key] && (state.data[key].length > 0 || (key === 'underGradCollegeJoined' && Object.keys(state.data[key]).length > 0))) {
                // count += state.data[key].length;
                if (key === 'underGradCollegeJoined' && (state.data[key].universityId > 0 || (state.data[key].otherUniversity && state.data[key].otherUniversity != ''))) {
                    count += 1; // Count underGradCollegeJoined as 1 if it has any data
                } else if (key === 'postGradCollegeJoined' && Array.isArray(state.data[key]) && state.data[key].length > 0) {
                    let pgCount = 0;
                    // Count only those postGradCollegeJoined entries which have valid universityId or otherUniversity
                    state.data[key].forEach((pgInst: any) => {
                        if (pgInst.universityId > 0 || (pgInst.otherUniversity && pgInst.otherUniversity != '')) {
                            pgCount += 1;
                        }
                    });
                    count += pgCount;
                } else if (key === 'schools' && Array.isArray(state.data[key]) && state.data[key].length > 0) {
                    let schoolCount = 0;
                    // Count only those schools which have valid schoolId/class10SchoolName or schoolName
                    state.data[key].forEach((school: any) => {
                        const hasValidSchoolId = school.schoolId > 0;
                        const hasValidClass10SchoolName = asText(school.class10SchoolName) !== '';
                        if (hasValidSchoolId || hasValidClass10SchoolName) {
                            schoolCount += 1;
                        }
                    });

                    count += schoolCount;
                }
            }
        });
        return count;
    }
    const kgToPgInstCount = getKgToPgInstCount();

    const getJourneyData = () => {
        const journeyData: any[] = [];

        // add schools data to journeyData
        if (state.data.schools && state.data.schools.length > 0) {
            state.data.schools.forEach((inst: any) => {
                const instituteName =
                    domainName(inst.schoolId, 'INSTITUTE_DOMAIN', state) || asText(inst.schoolName) || asText(inst.class10SchoolName);

                if (!instituteName) {
                    return; // Skip if institute name is not available
                }

                journeyData.push({
                    type: 'School',
                    dispType: `Class ${inst.classCode ?? ''}`,
                    name: instituteName,
                    startYear: inst.endYear,
                    endYear: inst.endYear,
                    details: inst
                });
            });
        }

        // separate code for UG & PG as they are stored differently in state
        if (state.data.underGradCollegeJoined && Object.keys(state.data.underGradCollegeJoined).length > 0) {
            const inst = state.data.underGradCollegeJoined;
            const instituteName = domainName(inst.universityId, 'INSTITUTE_DOMAIN', state) || asText(inst.otherUniversity);
            if (instituteName) {
                journeyData.push({
                    type: 'UG',
                    dispType: 'Under Graduate',
                    name: instituteName,
                    startYear: inst.startYear,
                    endYear: inst.endYear,
                    details: inst
                });
            }
        }
        if (state.data.postGradCollegeJoined && state.data.postGradCollegeJoined.length > 0) {
            state.data.postGradCollegeJoined.forEach((inst: any) => {
                const instituteName = domainName(inst.universityId, 'INSTITUTE_DOMAIN', state) || asText(inst.otherUniversity);
                if (!instituteName) {
                    return; // Skip if institute name is not available
                }

                journeyData.push({
                    type: 'PG',
                    dispType: 'Post Graduate',
                    name: instituteName,
                    startYear: inst.startYear,
                    endYear: inst.endYear,
                    details: inst
                });
            });
        }

        // Similar logic can be applied for jobs and achievements to add them to the journeyData array
        validJobs.forEach((job: any) => {
            const jobStartYear = job.startDate ? new Date(job.startDate).getFullYear() : null;
            const jobEndYear = job.isCurrent ? 'Present' : job.endDate ? new Date(job.endDate).getFullYear() : null;
            if (
                (job.companyId == null || job.companyId == 0) && !asText(job.otherCompany)
                && (job.instituteId == null || job.instituteId == 0) && !asText(job.otherInstitute)
            ) {
                return; // Skip if neither companyId nor otherCompany is provided
            }
            const name = job.jobCategory == 'TEACHING_AND_RESEARCH'
                ? (job.instituteId && job.instituteId > 0 ? domainName(job.instituteId, 'INSTITUTE_DOMAIN', state) || asText(job.otherInstitute) : asText(job.otherInstitute))
                : (job.companyId && job.companyId > 0 ? domainName(job.companyId, 'COMPANY_CODE', state) || asText(job.otherCompany) : asText(job.otherCompany));
            if (!name) {
                return; // Skip if name is not available
            }

            journeyData.push({
                type: 'job',
                dispType: asText(job.designation) || (job.jobCategory == 'WORKING_FOR_A_COMPANY' ? "Job" : domainName(job.jobCategory, 'WORK_STATUS_CODE', state) || 'Job'),
                name: name,
                startYear: jobStartYear ?? "",
                endYear: jobEndYear ?? "",
                details: job
            });
        });

        // Sort latest first (newest start/end year on top)
        journeyData.sort((a, b) => {
            const aStart = typeof a.startYear === 'number' ? a.startYear : new Date(a.startYear).getFullYear();
            const bStart = typeof b.startYear === 'number' ? b.startYear : new Date(b.startYear).getFullYear();
            if (aStart !== bStart) {
                return bStart - aStart;
            }

            const aEnd = a.endYear === 'Present' ? new Date().getFullYear() : (typeof a.endYear === 'number' ? a.endYear : new Date(a.endYear).getFullYear());
            const bEnd = b.endYear === 'Present' ? new Date().getFullYear() : (typeof b.endYear === 'number' ? b.endYear : new Date(b.endYear).getFullYear());
            return bEnd - aEnd;
        });

        return journeyData;
    };

    const checkForGaps = (data: any[]) => {
        if (!data || data.length < 2) {
            return false; // No gaps if less than 2 records
        }

        // data is newest-first; walk chronologically by comparing each older item with the newer one above it
        for (let i = 0; i < data.length - 1; i++) {
            const newerStartYear = typeof data[i].startYear === 'number' ? data[i].startYear : new Date(data[i].startYear).getFullYear();
            const olderEndYear = data[i + 1].endYear === 'Present' ? new Date().getFullYear() : (typeof data[i + 1].endYear === 'number' ? data[i + 1].endYear : new Date(data[i + 1].endYear).getFullYear());

            // Check if gap is more than 1 year between older end and newer start
            if (newerStartYear - olderEndYear > 1) {
                return true;
            }
        }
        return false;
    };

    const [journeyData, setJourneyData] = useState<any[]>([]);
    const [resumeFilePath, setResumeFilePath] = useState<string>(state.data.skillInfo?.resumeFilePath ?? '');
    const [resumeUploadedAt, setResumeUploadedAt] = useState<string>(state.data.skillInfo?.resumeUploadedAt ?? '');
    const RESUME_MODAL_ID = 'resumeManageModal';

    const openResumeModal = () => {
        const el = document.getElementById(RESUME_MODAL_ID);
        if (el) new Modal(el).show();
    };


    useEffect(() => {
        const data: any[] = getJourneyData() ?? [];
        setJourneyData(data);
        
        // Check for gaps in journey and update state
        const hasGaps = checkForGaps(data);
        setIsGapInJourney(hasGaps);
        
    }, [state.data.schools, state.data.underGradCollegeJoined, state.data.postGradCollegeJoined, validJobs]);

    // useEffect(() => {
    //         var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    //         var tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
    //             return new Tooltip(tooltipTriggerEl);
    //         });             
    // }, []);
    useEffect(() => {
        const tooltipEls: Element[] = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"].html-tooltip'));
        const tooltipList: Tooltip[] = [];

        tooltipEls.forEach((el) => {
            try {
                const rawTitle = el.getAttribute('data-bs-title') ?? el.getAttribute('title');
                // Only initialize tooltips with valid, non-empty titles
                if (rawTitle && rawTitle.trim() !== '' && rawTitle !== 'null') {
                    const tooltip = new Tooltip(el as HTMLElement, {
                        title: rawTitle,
                        trigger: 'hover',
                        html: true,
                        customClass: 'tooltip-html',
                        container: 'body',
                        delay: { show: 0, hide: 100 }
                    });
                    tooltipList.push(tooltip);
                }
            } catch (e) {
                console.warn('Tooltip initialization skipped:', e);
            }
        });

        return () => {
            tooltipList.forEach((tooltip) => {
                try {
                    if (tooltip && typeof (tooltip as any).dispose === 'function') {
                        (tooltip as any).dispose();
                    }
                } catch (e) {
                    // ignore dispose errors
                }
            });
        };
    }, [journeyData]);
    
    return (
        <>
            <div
                className="tab-pane fade active show"
                id="pills-journey"
                role="tabpanel"
                aria-labelledby="pills-journey-tab">
                <div className="pb-1">
                    <div className="row g-3 justify-content-center">

                        {/* <!-- Academics --> */}
                        <div className="col-md-3 col-sm-6">
                            <div
                                className={`border rounded-3 p-4 text-center h-100 bg-opacity-10 cursor-pointer ${!kgToPgInstCount || kgToPgInstCount<= 0 ? '' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => navigateToProfileTab('education')}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigateToProfileTab('education'); } }}
                            >
                                <div className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3 ratio-40">
                                <i className="bi bi-mortarboard-fill text-dark-blue font-23"></i>
                                </div>
                                <h6 className="font-16 font-500 text-gray-41 mb-2">Academics</h6>
                                <h5 className="font-20 font-600 text-dark-blue mb-1">{kgToPgInstCount} Education Details</h5>
                                <small className="font-14 font-400 text-gray-41">K-12 to PG</small>
                            </div>
                        </div>

                        {sessionState?.tabType != 'STUDENT' && (
                            <>
                                {/* <!-- Profession --> */}
                                <div className="col-md-3 col-sm-6">
                                    <div
                                        className={`border rounded-3 p-4 text-center h-100 bg-opacity-10 cursor-pointer ${!validJobs || validJobs.length === 0 ? '' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => navigateToProfileTab('work')}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigateToProfileTab('work'); } }}
                                    >
                                        <div className={`rounded-circle bg-success bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3 ratio-40`}>
                                        <i className="bi bi-briefcase-fill text-green fs-5"></i>
                                        </div>
                                        <h6 className="font-16 font-500 text-gray-41 mb-2">Profession</h6>
                                        <h5 className={`font-20 font-600 mb-1 ${!validJobs || validJobs.length === 0 ? 'text-danger' : 'text-green'}`}>{validJobs.length} Work Experience</h5>
                                        {mostRecentJob && (
                                            <small className="font-14 font-400 text-gray-41">
                                                {getJobDisplayName(mostRecentJob)} ({formatJobDates(mostRecentJob)})
                                            </small>
                                        )}
                                        {!mostRecentJob && (
                                            <a
                                                href={allowPerformAction() ? `work` : undefined}
                                                onClick={(e) => e.stopPropagation()}
                                                className={`text-skyblue text-decoration-underline small ${getActionElementClass()}`}
                                            >
                                                Add Details
                                            </a>
                                        )}
                                    </div>
                                </div>

                                {/* <!-- Achievements --> */}
                                <div className="col-md-3 col-sm-6">
                                    <div
                                        className={`border rounded-3 p-4 text-center h-100 bg-opacity-10 cursor-pointer ${!state.data.achievements || state.data.achievements.length === 0 ? 'bg-danger border-danger' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => navigateToProfileTab('achievements')}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigateToProfileTab('achievements'); } }}
                                    >
                                        <div className={`rounded-circle ${!state.data.achievements || state.data.achievements.length === 0 ? 'bg-danger' : 'bg-warning-subtle'} bg-opacity-25 d-inline-flex align-items-center justify-content-center mb-3 ratio-40`}>
                                        <i><img className={`${!state.data.achievements || state.data.achievements.length === 0 ? '' : 'filter-8c630c'}`} src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/icon-red-achievements.svg" width="24" alt=""/></i>
                                        </div>
                                        <h6 className="font-16 font-500 text-gray-41 mb-2">Achievements</h6>
                                        <h5 className={`font-20 font-600 mb-1 ${!state.data.achievements || state.data.achievements.length === 0 ? 'text-danger' : 'text-yellow-f8b018'}`}>{state.data.achievements.length} Achievements</h5>
                                        {(!state.data.achievements || state.data.achievements.length === 0) && (
                                            <a
                                                href={allowPerformAction() ? `/alumni/${state.data.id}/achievements` : undefined}
                                                onClick={(e) => e.stopPropagation()}
                                                className={`text-skyblue text-decoration-underline small ${getActionElementClass()}`}
                                            >
                                                Add Details
                                            </a>
                                        )}
                                        {(state.data.achievements && state.data.achievements.length > 0) && (
                                            // display 2 or 3 achievements based on the size of the title & show +n more if there are more than 3 achievements
                                            <div className="text-start mt-2">
                                                <span className="font-14 font-400 text-gray-41 d-flex justify-content-center">
                                                    {state.data.achievements.slice(0, 1).map((ach: any) => ach.title).join(', ') && (
                                                        <span className='text-truncate max-150 d-block w-auto me-2 html-tooltip' data-bs-toggle="tooltip" data-bs-title={`${state.data.achievements.slice(0, 1).map((ach: any) => ach.title).join(', ')}`}>
                                                            {state.data.achievements.slice(0, 1).map((ach: any) => ach.title).join(', ')}
                                                        </span>
                                                    )}
                                                    {state.data.achievements.length > 1 && (
                                                        <span className='html-tooltip' data-bs-toggle="tooltip" data-bs-title={`<ul class="list-unstyled mb-0">${state.data.achievements.slice(1).map((ach: any) => `<li>${ach.title}</li>`).join('')}</ul>`}>
                                                            {` +${state.data.achievements.length - 1} more`}
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* <!-- Skillset --> */}
                                <div className="col-md-3 col-sm-6">
                                    <div
                                        className={`border rounded-3 p-4 text-center h-100 bg-opacity-10 cursor-pointer ${!state.data.skills || state.data.skills.length === 0 ? 'bg-danger border-danger' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => navigateToProfileTab('skills')}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigateToProfileTab('skills'); } }}
                                    >
                                        <div className={`rounded-circle ${!state.data.skills || state.data.skills.length === 0 ? 'bg-danger' : 'bg-lite-purple-e8e6fd'} bg-opacity-25 d-inline-flex align-items-center justify-content-center mb-3 ratio-40`}>
                                        <i><img className={`${!state.data.skills || state.data.skills.length === 0 ? '' : 'filter-51499c'}`} src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/icon-red-blub.svg" width="24" alt="" /></i>
                                        </div>
                                        <h6 className="font-16 font-500 text-gray-41 mb-2">Skillset</h6>
                                        <h5 className={`font-20 font-600 mb-1 ${!state.data.skills || state.data.skills.length === 0 ? 'text-danger' : 'text-purple'}`}>{state.data.skills.length} Skills</h5>
                                        {(!state.data.skills || state.data.skills.length === 0) && (
                                            <a
                                                href={allowPerformAction() ? `/alumni/${state.data.id}/skills` : undefined}
                                                onClick={(e) => e.stopPropagation()}
                                                className={`text-skyblue text-decoration-underline small ${getActionElementClass()}`}
                                            >
                                                Add Details
                                            </a>
                                        )}
                                        {(state.data.skills && state.data.skills.length > 0) && (
                                            // display 2 or 3 skills and show +n more if there are more than 3 skills
                                            <div className="text-start mt-2">
                                                <span className="font-14 font-400 text-gray-41 d-flex justify-content-center">
                                                    {state.data.skills.slice(0, 1).map((skill: any) => skill.skillCode > 0 ? getDomainValueForCode(skill.skillCode, 'SKILL_CODE', state) : skill.otherSkill).join(', ') && (
                                                        <span className='text-truncate max-150 d-block w-auto me-2 html-tooltip' data-bs-toggle="tooltip" data-bs-title={`${state.data.skills.slice(0, 1).map((skill: any) => skill.skillCode > 0 ? getDomainValueForCode(skill.skillCode, 'SKILL_CODE', state) : skill.otherSkill).join(', ')}`}>
                                                            {state.data.skills.slice(0, 1).map((skill: any) => skill.skillCode > 0 ? getDomainValueForCode(skill.skillCode, 'SKILL_CODE', state) : skill.otherSkill).join(', ')}    
                                                        </span>
                                                    )}
                                                    {state.data.skills.length > 1 && (
                                                        <span className='html-tooltip' data-bs-toggle="tooltip" data-bs-title={`<ul class="list-unstyled mb-0">${state.data.skills.slice(1).map((skill: any) => `<li>${skill.skillCode > 0 ? getDomainValueForCode(skill.skillCode, 'SKILL_CODE', state) : skill.otherSkill}</li>`).join('')}</ul>`}>
                                                            {` +${state.data.skills.length - 1} more`}    
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                        
                        
                        {isGapInJourney && (
                            <div className="col-12">
                                <div className="alert alert-warning alert-dismissible fade show p-4" role="alert">
                                    <button className="btn btn-close text-warning-emphasis" type="button" data-bs-dismiss="alert"></button>
                                    <div className="row g-3">
                                        <div className="col-auto">
                                            <i className="bi bi-clock-fill font-24 text-warning-emphasis"></i>
                                        </div>
                                        <div className="col">
                                            <h5 className="font-16 font-500 text-warning-emphasis mb-1">Your Professional Journey</h5>
                                            <p className="font-14 font-400 mb-0 text-warning-emphasis lh-base">Complete timeline from schooling to your current engagement. Gaps highlight areas with missing information in your timeline.<br className="d-none d-md-block" /> Adding details helps others understand your journey better.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {/* Timeline View Section */}
                <div className="py-4 max-700 w-100 mx-auto">
                    <ul className="impact-flow mb-4">
                        {journeyData.map((item: any, index: number) => {
                            const isEducation = ['School', 'UG', 'PG'].includes(item.type);
                            const isJob = item.type === 'job';
                            const startYear = item.startYear ? (typeof item.startYear === 'number' ? item.startYear : new Date(item.startYear).getFullYear()) : "";
                            const endYear = item.endYear ? (item.endYear === 'Present' ? 'Present' : (typeof item.endYear === 'number' ? item.endYear : new Date(item.endYear).getFullYear())) : "";
                            
                            // if item.type is School, then show only endYear in dateRange
                            // const dateRange = `${item.type === 'School' ? endYear : startYear}${endYear !== startYear && endYear !== 'Present' ? ' - ' + endYear : endYear === 'Present' ? ' - Present' : ''}`;
                            // const dateRange = `${startYear}${endYear !== startYear && endYear !== 'Present' ? ' - ' + endYear : endYear === 'Present' ? ' - Present' : ''}`;
                            let dateRange = '';
                            if (item.type === 'School') {
                                dateRange = `${endYear}`;
                            } else if(item.type === 'job' && item.details?.jobCategory === 'ENTREPRENEURSHIP'){
                                if (item.details?.isCurrent) {
                                    dateRange = `${startYear} - Present`;
                                } else {
                                    dateRange = startYear ? `${startYear}` : "";
                                }
                            } else {
                                dateRange = `${startYear}${endYear !== startYear && endYear !== 'Present' ? ' - ' + endYear : endYear === 'Present' ? ' - Present' : ''}`;
                            }
                                
                            
                            // Calculate duration for education
                            const getDuration = () => {
                                // if (item.endYear === 'Present') return null;
                                if (item.endYear === 'Present' || !item.endYear || !item.startYear) return null;
                                const start = typeof item.startYear === 'number' ? item.startYear : new Date(item.startYear).getFullYear();
                                const end = typeof item.endYear === 'number' ? item.endYear : new Date(item.endYear).getFullYear();
                                const years = end - start;
                                // // if item.type is School, then show only endYear;
                                // if (item.type === 'School') {
                                //     return endYear;
                                // }
                                return years > 0 ? `${years} Year${years > 1 ? 's' : ''}` : null;
                            };

                            return (
                                <li key={index}>
                                    <div className="icon-wrapper">
                                        <div className={`icon ${isEducation ? 'bg-primary bg-opacity-10 border-0' : isJob ? 'bg-success bg-opacity-10 border-0' : ''}`}>
                                            {isEducation && (
                                                <i className="bi bi-mortarboard-fill text-dark-blue"></i>
                                            )}
                                            {isJob && (
                                                <i className="bi bi-briefcase-fill text-green"></i>
                                            )}
                                        </div>
                                    </div>

                                    <div className={`impact-flow-detail ${isEducation || isJob ? 'border-success' : ''} p-3 p-sm-4`}>
                                        <div className="row g-2">
                                            <div className="col">
                                                <h3 className="font-18 font-600 mb-2">
                                                    {/* {item.type === 'School' && 'School'} */}
                                                    {/* {item.type === 'UG' && 'Under Graduate'} */}
                                                    {/* {item.type === 'PG' && 'Post Graduate'} */}
                                                    {item.dispType || item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                                                    {/* {isJob && item.details?.designation} */}
                                                </h3>
                                                <div className="row g-2">
                                                    <div className="col-auto font-16 font-500">
                                                        <i className="icon-tick d-inline-block me-1"></i>
                                                        {item.name}
                                                        {isJob && item.details?.isCurrent && ' (Current Role)'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={`col-auto ${getDuration() ? 'text-end' : ''}`}>
                                                <div className="border rounded-3 px-3 py-1 font-14 font-500 text-gray-41">
                                                    {dateRange}
                                                </div>
                                                {getDuration() && (
                                                    <i className="font-14 mt-1 me-1 d-inline-block">{getDuration()}</i>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {canShowBenefits(state) && (
                    <div className="py-3 px-4 max-850 w-100 mx-auto lite-blue rounded-3 mb-5">
                        {/* Explore Benefits CTA Section */}
                        <div className="row g-3 align-items-center">
                            <div className="col-sm">
                                <div className="font-22 font-600 mb-0">Customize Your Experience</div>
                                <p className="font-16 font-400 mb-0">Set up your networking and pairing preferences and see what benefits await you.</p>
                            </div>
                            <div className="col-sm-auto">
                                <button onClick={() => {
                                    dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'selectedTab', value: 'benefits' } });
                                    navigate(`/alumni/${state.data.id}/profile/benefits`)
                                }} type="button" className="btn-dark-blue height-40" disabled={!allowPerformAction()}> 
                                    <span className="text-white text-nowrap">Explore Benefits</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
            {/* Resume Card */}
            <div className="max-850 w-100 mx-auto border border-success bg-success-subtle rounded-2 p-3">
                <div className="row g-3 align-items-center">
                    <div className="col-sm">
                        <div className="font-16 font-600 mb-1">Resume</div>
                        {resumeFilePath ? (
                            <p className="font-14 font-400 mb-0">
                                <i className="bi bi-file-earmark-text me-1 text-success" />
                                <a href={resumeFilePath} target="_blank" rel="noopener noreferrer" className="text-success-emphasis">
                                    Download Resume
                                </a>
                                {resumeUploadedAt && (
                                    <span className="text-muted font-13 ms-2">
                                        · Uploaded {new Date(resumeUploadedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                )}
                            </p>
                        ) : (
                            <p className="font-14 font-400 mb-0 text-muted">No resume uploaded yet</p>
                        )}
                    </div>
                    <div className="col-sm-auto">
                        <button
                            type="button"
                            className="btn btn-success rounded-pill px-4 btn-sm fw-semibold"
                            onClick={openResumeModal}
                            disabled={!allowPerformAction()}
                        >
                            <span className="text-white text-nowrap">Manage Resume</span>
                        </button>
                    </div>
                </div>
            </div>

            <ResumeManageModal
                modalId={RESUME_MODAL_ID}
                onSaved={(newUrl, uploadedAt) => {
                    setResumeFilePath(newUrl);
                    setResumeUploadedAt(uploadedAt);
                    dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'skillInfo.resumeFilePath', value: newUrl } });
                    dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'skillInfo.resumeUploadedAt', value: uploadedAt } });
                }}
            />

            {/* {isConfigAvailable && <EditProfileInformationModal userdata={{ model, profile }} />} */}
        </>
    );
};

export default Journey;
